/**
 * The Mapping tab (DOP-627): how the new file becomes the tape, three ways.
 *
 * - **Columns**: every tape column, how it is made and where it is read from.
 * - **Map**: the lineage as a graph, in the style of the one that opens from
 *   a chat answer: raw tabs → standardized tables → the loan → the tape, with
 *   the example file's tabs beside the new file's where the run recorded them.
 * - **SQL**: the frozen spine SQL the run applied, as the chat's "SQL" modal
 *   showed it.
 *
 * The first redesign folded Map and SQL into the column list; the user wanted
 * both back, so they are views of this tab rather than modals.
 */

import { Check, Copy } from "lucide-react";
import React, { useState } from "react";
import SqlHighlight from "../SqlHighlight";
import type { LoanTapePreviewColumn, SchemaGraphBlock } from "../Chat/Chat.types";
import ColumnGuide from "./ColumnGuide";
import TapeMapGraph from "./TapeMapGraph";

export const MAPPING_VIEWS = [
	{ id: "columns", label: "Columns" },
	{ id: "map", label: "Map" },
	{ id: "sql", label: "SQL" },
] as const;

export type MappingViewId = (typeof MAPPING_VIEWS)[number]["id"];

export const isMappingView = (value: string | null): value is MappingViewId =>
	MAPPING_VIEWS.some((view) => view.id === value);

export interface MappingViewProps {
	view: MappingViewId;
	onViewChange: (view: MappingViewId) => void;
	columns: readonly LoanTapePreviewColumn[];
	graph: SchemaGraphBlock | null;
	onOpenColumn: (column: LoanTapePreviewColumn) => void;
	/** How many loans the tape has, when the run says. */
	loans?: number | null;
	/** The new file and the example file, by name, for the Map's legend. */
	sourceName?: string | null;
	exampleName?: string | null;
	/** The deal each file sits in, which is what its tabs are called. */
	sourceLabel?: string | null;
	exampleLabel?: string | null;
}

const EMPTY =
	"rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]";

const SqlView: React.FC<{ sql: string }> = ({ sql }) => {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(sql);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard refused (permissions, insecure origin): the SQL is on
			// screen to select by hand.
		}
	};
	return (
		<div className="rounded-xl border border-gray-200 bg-white">
			<div className="flex items-center justify-between border-b border-[#f1f2f4] px-4 py-2.5">
				<p className="text-[13px] text-[#5B6472]">
					The query that builds the tape from the standardized fields.
				</p>
				<button
					type="button"
					onClick={() => void copy()}
					className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-medium text-[#1F2937] hover:bg-gray-50"
				>
					{copied ? (
						<Check className="h-3.5 w-3.5" aria-hidden />
					) : (
						<Copy className="h-3.5 w-3.5" aria-hidden />
					)}
					{copied ? "Copied" : "Copy SQL"}
				</button>
			</div>
			<div className="max-h-[66vh] overflow-auto p-4">
				<SqlHighlight sql={sql} />
			</div>
		</div>
	);
};

const MappingView: React.FC<MappingViewProps> = ({
	view,
	onViewChange,
	columns,
	graph,
	onOpenColumn,
	loans = null,
	sourceName,
	exampleName,
	sourceLabel,
	exampleLabel,
}) => {
	const sql = graph?.sql?.trim() ? graph.sql : null;

	let body: React.ReactNode;
	if (view === "map") {
		body =
			graph && graph.nodes.length > 0 ? (
				<TapeMapGraph
					graph={graph}
					columns={columns}
					loans={loans}
					sourceName={sourceName}
					exampleName={exampleName}
					sourceLabel={sourceLabel}
					exampleLabel={exampleLabel}
				/>
			) : (
				<p className={EMPTY}>No map for this run. Run the tape to build one.</p>
			);
	} else if (view === "sql") {
		body = sql ? (
			<SqlView sql={sql} />
		) : (
			<p className={EMPTY}>No SQL for this run. Run the tape to build one.</p>
		);
	} else {
		body = <ColumnGuide columns={columns} onOpenColumn={onOpenColumn} />;
	}

	return (
		<div className="flex flex-col gap-3.5">
			<div
				role="group"
				aria-label="Mapping view"
				className="inline-flex self-start rounded-lg border border-gray-200 bg-white p-0.5"
			>
				{MAPPING_VIEWS.map((option) => (
					<button
						key={option.id}
						type="button"
						aria-pressed={view === option.id}
						onClick={() => onViewChange(option.id)}
						className={`h-8 rounded-md px-3.5 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${
							view === option.id
								? "bg-[#004C4B] text-white"
								: "text-[#3f4247] hover:bg-gray-50"
						}`}
					>
						{option.label}
					</button>
				))}
			</div>
			{body}
		</div>
	);
};

export default MappingView;
